import type { SvgIconComponent } from '@mui/icons-material'
import type { ChicletTone } from 'sutra/components/Chiclet/Chiclet'

import type {
  BreakdownOption,
  MetricType,
  TimeBucketOption,
} from 'features/charts-v2/types'
import type {
  Breakdown,
  FlowColorMode,
} from 'features/charts-v2/types/filter-hash/v2'

export enum ChartTypeV2 {
  BAR_HORIZONTAL = 'horizontal_bar_chart',
  BAR_HORIZONTAL_STACKED = 'stacked_bar_chart',
  BUBBLE = 'bubble_chart',
  HEATMAP = 'heatmap_chart',
  LINE = 'line_chart',
  PIE = 'pie_chart',
  RIDGELINE = 'ridgeline_chart',
  SANKEY = 'sankey_chart',
  SCATTER = 'scatter_chart',
  TABLE = 'table_chart',
  TREEMAP = 'treemap_chart',

  DEPRECATED_V1 = 'deprecated_v1_chart',
  UNKNOWN = 'unknown',

  // TODO: Not built yet
  AREA = 'area_chart',
  AREA_PERCENT = 'percent_area_chart',
  AREA_STACKED = 'stacked_area_chart',
  BAR_VERTICAL = 'vertical_bar_chart',
  BAR_VERTICAL_STACKED = 'vertical_stacked_bar_chart',
  HISTOGRAM = 'histogram_chart',
}

// Chart types that support pagination — one row per breakdown value, so the
// horizontal bar family and the ridgeline (distribution) chart.
// Shared by the normal charts page (ChartCommandBar) and the custom-dashboard
// embeddable so a new paginatable type is added in exactly one place.
export const PAGINATABLE_CHART_TYPES: readonly ChartTypeV2[] = [
  ChartTypeV2.BAR_HORIZONTAL,
  ChartTypeV2.BAR_HORIZONTAL_STACKED,
  ChartTypeV2.RIDGELINE,
]

// A Sankey click names a flow between two breakdown values, but "Set Alert" and
// "View in catalog" each take one primary breakdown value, so the context menu
// offers them for every chart type except this one.
export const hasSingleValueActions = (chartType: ChartTypeV2): boolean =>
  chartType !== ChartTypeV2.SANKEY

// Chart types with a real time axis — the only ones `timeBucket` (day/week/
// month/quarter/year granularity) means anything for. Shared by Container.tsx
// (which chart types get the time-bucket control) and the v1<->v2 filter
// translator (translate-v1-filters-to-panel.ts), which must not let a
// persisted `timeInterval` land on a panel outside this set — every other
// chart strategy (Bar, StackedBar, Pie, Treemap, ...) still forwards a
// non-null `timeBucket` straight into its sort comparator, which then parses
// category labels as dates and silently stops sorting by count.
export const TIME_BUCKET_CHART_TYPES: readonly ChartTypeV2[] = [
  ChartTypeV2.LINE,
  ChartTypeV2.AREA,
  ChartTypeV2.AREA_STACKED,
  ChartTypeV2.AREA_PERCENT,
]

export interface ChartTypeConfigV2 {
  id: ChartTypeV2
  label: string
}

export const CHART_TYPE_CONFIGS_V2: ChartTypeConfigV2[] = [
  { id: ChartTypeV2.BAR_HORIZONTAL, label: 'Bar' },
  { id: ChartTypeV2.LINE, label: 'Line' },
  { id: ChartTypeV2.BAR_HORIZONTAL_STACKED, label: 'Stacked Bar' },
  { id: ChartTypeV2.BUBBLE, label: 'Bubble' },
  { id: ChartTypeV2.TABLE, label: 'Table' },
  { id: ChartTypeV2.SCATTER, label: 'Scatter' },
  { id: ChartTypeV2.PIE, label: 'Pie' },

  // TODO: Added back on 2/25/26
  { id: ChartTypeV2.HEATMAP, label: 'Heatmap' },
  { id: ChartTypeV2.TREEMAP, label: 'Treemap' },
  { id: ChartTypeV2.RIDGELINE, label: 'Distribution' },
  { id: ChartTypeV2.SANKEY, label: 'Sankey' },

  // Disabled for initial launch:
  // { id: ChartTypeV2.BAR_VERTICAL, label: 'Vertical Bar' },
  // { id: ChartTypeV2.AREA, label: 'Area' },
  // { id: ChartTypeV2.AREA_STACKED, label: 'Stacked Area' },
  // { id: ChartTypeV2.AREA_PERCENT, label: 'Percent Area' },
  // { id: ChartTypeV2.HISTOGRAM, label: 'Histogram' },
]

export interface ChicletConfig {
  icon: SvgIconComponent | null
  text: string
  tone: ChicletTone
}

/**
 * Configuration for metric and breakdown labels per chart type
 */
export interface DataLabelConfig {
  metrics: {
    primary: string | null
    secondary: string | null
    tertiary: string | null
    quaternary: string | null
  }
  breakdowns: {
    primary: string | null
    secondary: string | null
    tertiary: string | null
  }
}

/**
 * Labels for metrics and breakdowns for each chart type
 */
export const CHART_DATA_LABELS: Record<ChartTypeV2, DataLabelConfig> = {
  // DONE:
  [ChartTypeV2.BUBBLE]: {
    metrics: {
      primary: 'X-Axis',
      secondary: 'Y-Axis',
      tertiary: 'Bubble size',
      quaternary: null,
    },
    breakdowns: {
      primary: 'Bubbles',
      secondary: null,
      tertiary: null,
    },
  },
  [ChartTypeV2.BAR_HORIZONTAL_STACKED]: {
    metrics: {
      primary: 'Bar size',
      secondary: null,
      tertiary: null,
      quaternary: null,
    },
    breakdowns: {
      primary: 'Bars',
      secondary: 'Bar segments',
      tertiary: null,
    },
  },
  [ChartTypeV2.BAR_HORIZONTAL]: {
    metrics: {
      primary: 'Bar size',
      secondary: null,
      tertiary: null,
      quaternary: null,
    },
    breakdowns: {
      primary: 'Bars',
      secondary: 'Bar segments',
      tertiary: null,
    },
  },
  [ChartTypeV2.RIDGELINE]: {
    metrics: {
      primary: 'Distribution of',
      secondary: null,
      tertiary: null,
      quaternary: null,
    },
    breakdowns: {
      primary: 'Ridges',
      secondary: null,
      tertiary: null,
    },
  },
  [ChartTypeV2.SANKEY]: {
    metrics: {
      primary: 'Flow size',
      secondary: null,
      tertiary: null,
      quaternary: null,
    },
    breakdowns: {
      primary: 'Flow start',
      secondary: 'Flow stage',
      tertiary: 'Flow end',
    },
  },
  [ChartTypeV2.LINE]: {
    metrics: {
      primary: 'Line datapoints',
      secondary: 'Area datapoints',
      tertiary: null,
      quaternary: null,
    },
    breakdowns: {
      primary: 'Lines',
      secondary: 'Areas',
      tertiary: null,
    },
  },
  [ChartTypeV2.SCATTER]: {
    metrics: {
      primary: 'X-Axis',
      secondary: 'Y-Axis',
      tertiary: null,
      quaternary: null,
    },
    breakdowns: {
      primary: 'Bubbles',
      secondary: null,
      tertiary: null,
    },
  },
  [ChartTypeV2.PIE]: {
    metrics: {
      primary: 'Slice size',
      secondary: null,
      tertiary: null,
      quaternary: null,
    },
    breakdowns: { primary: 'Slices', secondary: null, tertiary: null },
  },
  [ChartTypeV2.TABLE]: {
    metrics: {
      primary: 'Column one',
      secondary: 'Column two',
      tertiary: 'Column three',
      quaternary: 'Column four',
    },
    breakdowns: {
      primary: 'Row',
      secondary: 'Row group',
      tertiary: 'Row sub-group',
    },
  },
  [ChartTypeV2.HEATMAP]: {
    metrics: {
      primary: 'Color Intensity',
      secondary: 'Additional data label',
      tertiary: null,
      quaternary: null,
    },
    breakdowns: { primary: 'X-Axis', secondary: 'Y-Axis', tertiary: null },
  },
  [ChartTypeV2.TREEMAP]: {
    metrics: {
      primary: 'Box size',
      secondary: 'Box color',
      tertiary: 'Additional data label',
      quaternary: 'Additional data label',
    },
    breakdowns: {
      primary: 'Boxes',
      secondary: 'Color',
      tertiary: 'Nesting',
    },
  },

  // TODO:
  [ChartTypeV2.HISTOGRAM]: {
    metrics: {
      primary: 'Frequency',
      secondary: null,
      tertiary: null,
      quaternary: null,
    },
    breakdowns: { primary: 'Bins', secondary: 'Color', tertiary: 'Series' },
  },
  [ChartTypeV2.AREA]: {
    metrics: {
      primary: 'Y-Axis',
      secondary: 'Secondary Y-Axis',
      tertiary: null,
      quaternary: null,
    },
    breakdowns: { primary: 'X-Axis', secondary: 'Color', tertiary: 'Series' },
  },
  [ChartTypeV2.BAR_VERTICAL]: {
    metrics: {
      primary: 'Y-Axis',
      secondary: 'Secondary Y-Axis',
      tertiary: null,
      quaternary: null,
    },
    breakdowns: { primary: 'X-Axis', secondary: 'Color', tertiary: 'Series' },
  },
  [ChartTypeV2.AREA_STACKED]: {
    metrics: {
      primary: 'Y-Axis',
      secondary: 'Secondary Y-Axis',
      tertiary: null,
      quaternary: null,
    },
    breakdowns: { primary: 'X-Axis', secondary: 'Stack', tertiary: 'Series' },
  },
  [ChartTypeV2.AREA_PERCENT]: {
    metrics: {
      primary: 'Percentage',
      secondary: null,
      tertiary: null,
      quaternary: null,
    },
    breakdowns: { primary: 'X-Axis', secondary: 'Stack', tertiary: 'Series' },
  },
  [ChartTypeV2.DEPRECATED_V1]: {
    metrics: {
      primary: null,
      secondary: null,
      tertiary: null,
      quaternary: null,
    },
    breakdowns: { primary: null, secondary: null, tertiary: null },
  },
  [ChartTypeV2.UNKNOWN]: {
    metrics: {
      primary: null,
      secondary: null,
      tertiary: null,
      quaternary: null,
    },
    breakdowns: { primary: null, secondary: null, tertiary: null },
  },
  [ChartTypeV2.BAR_VERTICAL_STACKED]: {
    metrics: {
      primary: null,
      secondary: null,
      tertiary: null,
      quaternary: null,
    },
    breakdowns: { primary: null, secondary: null, tertiary: null },
  },
}

/**
 * Chart preset - applies specific metric/breakdown/chart type defaults
 */
export interface ChartPreset {
  id: string
  label: string
  description: string
  chartType: ChartTypeV2
  metrics: MetricType[]
  defaultPrimaryBreakdown: BreakdownOption | null
  secondaryBreakdown: BreakdownOption | null
  /** The full breakdown list, for presets the two ungrouped ids above cannot
   *  express (grouped breakdowns, a third breakdown). When set, panel creation
   *  and `applyPresetToPanel` use it verbatim; keep the two ids in step with
   *  its first two entries for the callers that still read them. */
  breakdowns?: Breakdown[]
  timeBucket: TimeBucketOption | null
  enabled: boolean
  icon: React.ReactNode | null
  chiclets: ChicletConfig[]
  /** When set, `{primary}` is replaced with the primary breakdown's display
   *  label when computing the chart title. Falls back to `label` when no
   *  primary breakdown is resolvable. See `resolvePresetTitle` in ChartPresets. */
  labelTemplate?: string
  /** When set, `{primary}` is replaced with the primary breakdown's lowercase
   *  label when computing the card description. Falls back to `description`. */
  descriptionTemplate?: string
  /** When set, the preset forces this Sankey color mode on panel creation and
   *  on preset-switch. Omitted presets leave the panel's current mode alone
   *  (effective default is `'origin'` — see the `flowColorMode` trap note in
   *  this feature's AGENTS.md). */
  flowColorMode?: FlowColorMode
}

/**
 * Chart preset group - organizes presets by user intent
 */
export interface ChartPresetGroup {
  key: string
  label: string
  icon: React.ReactNode
  presets: ChartPreset[]
}
